import { FormikProvider, useFormik } from "formik"
import * as Yup from "yup"
import { toast } from "react-toastify"
import { Box, Grid, Divider } from "@material-ui/core"
import NCModal from "../../shared/NCModal"
import NCButton from "../../shared/NCButton"
import FormField from "../FormField"
import { makeStyles } from "@material-ui/core"
import { useQueryClient } from "react-query"
import NCDatePicker from "../../shared/NCDatePicker"
import NCTimePicker from "../../shared/NCTimePicker"
import NCText from "../../shared/NCText"
import NCAutocomplete from "../../shared/NCAutocomplete"
import { useDriverAvailability } from "../../services/mutations"
import NCSvgIcon from "../../shared/NCSvgIcon"
import { AddIcon, DeleteIcon } from "../../libs/icons"
import { useTranslation } from "react-i18next"
import { IconButton } from "@material-ui/core"
import { useState, useEffect } from "react"
import API from "../../libs/api"
import { API_PATH } from "../../libs/config"

const DAYS_OF_WEEK = [
  { id: 0, name: "Monday", short: "Mon" },
  { id: 1, name: "Tuesday", short: "Tue" },
  { id: 2, name: "Wednesday", short: "Wed" },
  { id: 3, name: "Thursday", short: "Thu" },
  { id: 4, name: "Friday", short: "Fri" },
  { id: 5, name: "Saturday", short: "Sat" },
  { id: 6, name: "Sunday", short: "Sun" }
]

// ---- Timezone helpers (Intl-based, no extra dependency) ----
// The backend stores availability times as plain UTC HH:MM. We display and edit
// them in the DRIVER's timezone (from their User record), converting both ways.

const browserTz = () => Intl.DateTimeFormat().resolvedOptions().timeZone

// Wall-clock parts (year..second) of `date` expressed in IANA `tz`
const getTzParts = (date, tz) => {
  const parts = {}
  new Intl.DateTimeFormat("en-US", {
    timeZone: tz,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false
  })
    .formatToParts(date)
    .forEach(p => {
      if (p.type !== "literal") parts[p.type] = parseInt(p.value, 10)
    })
  if (parts.hour === 24) parts.hour = 0 // Intl can emit "24" for midnight
  return parts
}

// Offset (ms) of `tz` at the given UTC timestamp
const tzOffsetMs = (utcTs, tz) => {
  const p = getTzParts(new Date(utcTs), tz)
  const asUTC = Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, p.second)
  return asUTC - utcTs
}

// Backend UTC "HH:MM" -> "YYYY-MM-DD HH:MM" wall clock in `tz` (baseDate anchors DST)
const utcToTzDateTime = (utcTime, tz, baseDate = "2000-01-01") => {
  if (!utcTime) return ""
  const [h, m] = utcTime.split(":").map(Number)
  const [Y, M, D] = baseDate.split("-").map(Number)
  const p = getTzParts(new Date(Date.UTC(Y, M - 1, D, h, m)), tz)
  return `${p.year}-${String(p.month).padStart(2, "0")}-${String(p.day).padStart(2, "0")} ${String(p.hour).padStart(2, "0")}:${String(p.minute).padStart(2, "0")}`
}

// Backend UTC "HH:MM" -> "HH:MM" wall clock in `tz`
const utcToTzTime = (utcTime, tz, baseDate = "2000-01-01") => {
  if (!utcTime) return ""
  const [h, m] = utcTime.split(":").map(Number)
  const [Y, M, D] = baseDate.split("-").map(Number)
  const p = getTzParts(new Date(Date.UTC(Y, M - 1, D, h, m)), tz)
  return `${String(p.hour).padStart(2, "0")}:${String(p.minute).padStart(2, "0")}`
}

// Normalize a picker value ("2026-08-14 09:00" or "2026-08-14 09:30 AM") to "YYYY-MM-DDTHH:MM"
const normalizePickerValue = s => {
  if (!s) return null
  const m = String(s).trim().match(/^(\d{4})-(\d{2})-(\d{2})[ T](\d{1,2}):(\d{2})(?:\s*(AM|PM))?$/i)
  if (!m) return null
  let h = parseInt(m[4], 10)
  const mm = parseInt(m[5], 10)
  const ap = (m[6] || "").toUpperCase()
  if (ap === "PM" && h < 12) h += 12
  if (ap === "AM" && h === 12) h = 0
  return `${m[1]}-${m[2]}-${m[3]}T${String(h).padStart(2, "0")}:${String(mm).padStart(2, "0")}`
}

// Wall-clock datetime in `tz` -> backend UTC "HH:MM"
const tzDateTimeToUtcTime = (s, tz) => {
  const norm = normalizePickerValue(s)
  if (!norm) return ""
  const [datePart, timePart] = norm.split("T")
  const [Y, M, D] = datePart.split("-").map(Number)
  const [h, m] = timePart.split(":").map(Number)
  const utcTs = Date.UTC(Y, M - 1, D, h, m)
  const offset = tzOffsetMs(utcTs, tz)
  const d = new Date(utcTs - offset)
  return `${String(d.getUTCHours()).padStart(2, "0")}:${String(d.getUTCMinutes()).padStart(2, "0")}`
}

const useStyles = makeStyles({
  dayRow: {
    padding: "12px 16px",
    borderRadius: "8px",
    marginBottom: "8px",
    backgroundColor: "#F9FAFB",
    border: "1px solid #E5E7EB"
  },
  dayLabel: {
    fontWeight: 600,
    minWidth: "80px",
    color: "#374151"
  },
  timeSlot: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    marginBottom: "8px"
  },
  addSlotBtn: {
    color: "#1EAAC4",
    fontSize: "12px",
    padding: "4px 8px",
    textTransform: "none"
  },
  exceptionCard: {
    padding: "12px",
    borderRadius: "8px",
    backgroundColor: "#FEF3C7",
    border: "1px solid #FCD34D",
    marginBottom: "8px"
  },
  sectionTitle: {
    fontWeight: 600,
    fontSize: "14px",
    color: "#374151",
    marginBottom: "12px",
    marginTop: "16px"
  }
})

const AppointmentModal = ({
  modalState = false,
  handleClose,
  allData,
  refetchDriverAppointments,
  setSearch,
  isLoading
}) => {
  const { t } = useTranslation();
  const cls = useStyles()
  const queryClient = useQueryClient()
  const driverAvailability = useDriverAvailability()
  const [loadingSchedule, setLoadingSchedule] = useState(false)
  // Driver's timezone — defaults to browser tz until a driver's schedule loads
  const [driverTz, setDriverTz] = useState(() => browserTz())
  
  // Initialize weekly schedule with empty slots for each day
  const initialWeeklySlots = DAYS_OF_WEEK.reduce((acc, day) => {
    acc[day.id] = [{ start_time: "", end_time: "" }]
    return acc
  }, {})

  // Reset form when modal closes or opens
  const resetForm = () => {
    formik.resetForm({
      values: {
        driver: "",
        effective_from: new Date().toISOString().split("T")[0],
        weekly_slots: { ...initialWeeklySlots },
        exception_dates: []
      }
    })
  }

  // Clear form data when modal closes
  useEffect(() => {
    if (!modalState) {
      resetForm()
    }
  }, [modalState])

  const formik = useFormik({
    initialValues: {
      driver: "",
      effective_from: new Date().toISOString().split("T")[0],
      weekly_slots: initialWeeklySlots,
      exception_dates: []
    },
    validationSchema: Yup.object().shape({
      driver: Yup.string().required(t("appointments.driver_name_required")),
      effective_from: Yup.date().typeError("Use format(MM/dd/YYYY)").required("Effective date is required"),
      exception_dates: Yup.array().of(
        Yup.object().shape({
          date: Yup.mixed().test(
            "date-required",
            "Date is required",
            function(value) {
              const { type } = this.parent
              // Only validate if type is filled
              if (type) {
                return !!value
              }
              return true
            }
          ),
          type: Yup.mixed().test(
            "type-required",
            "Exception type is required",
            function(value) {
              const { date } = this.parent
              // Only validate if date is filled
              if (date) {
                return !!value
              }
              return true
            }
          ),
          start_time: Yup.string().when("type", {
            is: "custom_hours",
            then: (schema) => schema.required("Start time is required for custom hours"),
            otherwise: (schema) => schema
          }),
          end_time: Yup.string().when("type", {
            is: "custom_hours",
            then: (schema) => schema.required("End time is required for custom hours"),
            otherwise: (schema) => schema
          })
        })
      )
    }),
    enableReinitialize: true,
    onSubmit(values) {
      // Helper function to format time to HH:MM (24-hour UTC).
      // The picker value is a wall-clock datetime in the driver's timezone —
      // convert it back to UTC so the backend stores the same instant.
      const formatTime = (timeValue) => {
        if (!timeValue) return null
        return tzDateTimeToUtcTime(timeValue, driverTz)
      }

      // Validate all time ranges before submission
      let hasInvalidTimeRange = false

      // Check weekly slots
      Object.entries(values.weekly_slots).forEach(([, slots]) => {
        slots.forEach((slot) => {
          if (slot.start_time && slot.end_time) {
            const valid = validateTimeRange(slot.start_time, slot.end_time)
            // eslint-disable-next-line no-console
            console.log("[validate]", slot.start_time, slot.end_time, valid)
            if (!valid) hasInvalidTimeRange = true
          }
        })
      })

      // Check exception dates
      values.exception_dates.forEach((exc) => {
        if (exc.type === "custom_hours" && exc.start_time && exc.end_time) {
          const valid = validateTimeRange(exc.start_time, exc.end_time)
          // eslint-disable-next-line no-console
          console.log("[validate-exc]", exc.start_time, exc.end_time, valid)
          if (!valid) hasInvalidTimeRange = true
        }
      })

      if (hasInvalidTimeRange) {
        toast.error("Please fix time ranges: End time cannot be earlier than start time")
        return
      }

      // Filter out empty time slots and format data for backend
      const weeklySlots = Object.entries(values.weekly_slots)
        .map(([dayOfWeek, slots]) => 
          slots
            .filter(slot => slot.start_time && slot.end_time)
            .map(slot => ({
              day_of_week: parseInt(dayOfWeek),
              start_time: formatTime(slot.start_time),
              end_time: formatTime(slot.end_time)
            }))
        )
        .flat()

      const payload = {
        driver: values.driver,
        effective_from: values.effective_from,
        weekly_slots: weeklySlots,
        exception_dates: values.exception_dates
          .filter(exc => exc.date)
          .map(exc => ({
            ...exc,
            start_time: exc.type === "custom_hours" ? formatTime(exc.start_time) : null,
            end_time: exc.type === "custom_hours" ? formatTime(exc.end_time) : null
          }))
      }
      
      driverAvailability.mutate(payload, {
        onSuccess: () => {
          toast.success(t("appointments.added_success"))
          queryClient.invalidateQueries(["getDriverAvailability"])
          refetchDriverAppointments();
          handleClose()
        },
        onError: (error) => {
          toast.error(error?.message || "Failed to save availability")
        }
      })
    }
  })

  // Fetch existing schedule when driver is selected
  const fetchDriverSchedule = async (driverId) => {
    if (!driverId) return
    
    setLoadingSchedule(true)
    try {
      const response = await API.get(`${API_PATH.WEEKLY_AVAILABILITY}?driver_id=${driverId}`)
      const scheduleData = response?.data?.data?.results?.[0]
      
      if (scheduleData) {
        // Driver's saved timezone (from their User) — show/edit times in this tz
        const tz = scheduleData.timezone || browserTz()
        setDriverTz(tz)

        // Convert backend weekly_slots to frontend format (group by day)
        const weeklySlots = { ...initialWeeklySlots }
        scheduleData.weekly_slots?.forEach(slot => {
          const dayId = slot.day_of_week
          if (!weeklySlots[dayId]) {
            weeklySlots[dayId] = []
          }
          if (weeklySlots[dayId].length === 1 && !weeklySlots[dayId][0].start_time) {
            weeklySlots[dayId] = []
          }
          
          // Convert UTC time strings (HH:MM) to the driver's timezone for display
          const baseDate = scheduleData.effective_from || "2000-01-01"
          const startStr = utcToTzDateTime(slot.start_time, tz, baseDate)
          const endStr = utcToTzDateTime(slot.end_time, tz, baseDate)

          weeklySlots[dayId].push({
            start_time: startStr,
            end_time: endStr
          })
        })
        
        // Ensure every day has at least one empty slot
        Object.keys(weeklySlots).forEach(dayId => {
          if (weeklySlots[dayId].length === 0) {
            weeklySlots[dayId] = [{ start_time: "", end_time: "" }]
          }
        })
        
        // Convert exception dates UTC times to the driver's timezone for display
        const formattedExceptions = scheduleData.exception_dates?.map(exc => ({
          ...exc,
          start_time: exc.start_time ? `2000-01-01 ${utcToTzTime(exc.start_time, tz, exc.date)}` : "",
          end_time: exc.end_time ? `2000-01-01 ${utcToTzTime(exc.end_time, tz, exc.date)}` : ""
        })) || []
        
        formik.setValues({
          driver: driverId,
          effective_from: scheduleData.effective_from || new Date().toISOString().split("T")[0],
          weekly_slots: weeklySlots,
          exception_dates: formattedExceptions
        })
      } else {
        // No existing data - reset to initial values with selected driver
        formik.setValues({
          driver: driverId,
          effective_from: new Date().toISOString().split("T")[0],
          weekly_slots: { ...initialWeeklySlots },
          exception_dates: []
        })
      }
    } catch (error) {
      // Error fetching driver schedule - reset to initial values
      formik.setValues({
        driver: driverId,
        effective_from: new Date().toISOString().split("T")[0],
        weekly_slots: { ...initialWeeklySlots },
        exception_dates: []
      })
    } finally {
      setLoadingSchedule(false)
    }
  }

  // Add time slot to specific day
  const handleAddTimeSlot = (dayId) => {
    const currentSlots = formik.values.weekly_slots[dayId] || []
    formik.setFieldValue(`weekly_slots.${dayId}`, [
      ...currentSlots,
      { start_time: "", end_time: "" }
    ])
  }

  // Validate time range — compares only time-of-day (HH:MM), not dates.
  // Overnight slots (e.g. 22:00 → 02:00) are always valid.
  const validateTimeRange = (startTime, endTime) => {
    if (!startTime || !endTime) return true
    
    const start = new Date(startTime)
    const end = new Date(endTime)
    
    if (isNaN(start.getTime()) || isNaN(end.getTime())) return true
    
    // Compare only the time-of-day portion
    const startMin = start.getHours() * 60 + start.getMinutes()
    const endMin = end.getHours() * 60 + end.getMinutes()
    
    // Overnight: end is earlier in clock time (wraps past midnight) — always valid
    if (endMin <= startMin) return true
    
    return true
  }

  // Handle time change with validation
  const handleTimeChange = (dayId, slotIndex, field, value) => {
    // Get the current slot values
    const currentSlot = formik.values.weekly_slots[dayId][slotIndex]
    const startTime = field === "start_time" ? value : currentSlot.start_time
    const endTime = field === "end_time" ? value : currentSlot.end_time
    
    // Validate if both times are set
    if (startTime && endTime && !validateTimeRange(startTime, endTime)) {
      toast.error("End time cannot be earlier than start time")
      
      // Force the field to clear by setting to empty string
      // This triggers NCTimePicker's useEffect to reset its internal state
      if (field === "end_time") {
        const fieldPath = `weekly_slots.${dayId}[${slotIndex}].end_time`
        // Set to null first to ensure change detection, then to empty string
        formik.setFieldValue(fieldPath, null)
        setTimeout(() => {
          formik.setFieldValue(fieldPath, "")
        }, 0)
      }
      return
    }
    
    // Set the value if validation passes
    const fieldPath = `weekly_slots.${dayId}[${slotIndex}].${field}`
    formik.setFieldValue(fieldPath, value)
  }

  // Handle exception time change with validation
  const handleExceptionTimeChange = (index, field, value) => {
    // Get the current exception values
    const currentException = formik.values.exception_dates[index]
    const startTime = field === "start_time" ? value : currentException.start_time
    const endTime = field === "end_time" ? value : currentException.end_time
    
    // Validate if both times are set
    if (startTime && endTime && !validateTimeRange(startTime, endTime)) {
      toast.error("End time cannot be earlier than start time")
      
      // Force the field to clear by setting to empty string
      // This triggers NCTimePicker's useEffect to reset its internal state
      if (field === "end_time") {
        const fieldPath = `exception_dates[${index}].end_time`
        // Set to null first to ensure change detection, then to empty string
        formik.setFieldValue(fieldPath, null)
        setTimeout(() => {
          formik.setFieldValue(fieldPath, "")
        }, 0)
      }
      return
    }
    
    // Set the value if validation passes
    const fieldPath = `exception_dates[${index}].${field}`
    formik.setFieldValue(fieldPath, value)
  }

  // Remove time slot from specific day
  const handleRemoveTimeSlot = (dayId, slotIndex) => {
    const currentSlots = formik.values.weekly_slots[dayId]
    if (currentSlots.length > 1) {
      const updatedSlots = currentSlots.filter((_, idx) => idx !== slotIndex)
      formik.setFieldValue(`weekly_slots.${dayId}`, updatedSlots)
    }
  }


  // Add exception date
  const handleAddException = () => {
    formik.setFieldValue("exception_dates", [
      ...formik.values.exception_dates,
      { date: "", type: "unavailable", start_time: "", end_time: "", reason: "" }
    ])
  }

  // Remove exception date
  const handleRemoveException = (index) => {
    const updated = formik.values.exception_dates.filter((_, idx) => idx !== index)
    formik.setFieldValue("exception_dates", updated)
  }

  // Clear specific day's time slots
  const handleClearDay = (dayId) => {
    const updatedSlots = { ...formik.values.weekly_slots }
    updatedSlots[dayId] = [{ start_time: "", end_time: "" }]
    formik.setFieldValue("weekly_slots", updatedSlots)
  }

  const requestModal = (
    <FormikProvider value={formik}>
      <Box>
        <Grid container spacing={2}>
            {/* Driver Selection */}
            <Grid item xs={12}>
              <NCText style={{ color: "#9CA3AF", fontSize: "12px", marginBottom: "4px" }}>
                {t("appointments.select_driver_for_avail")}
              </NCText>
              <FormField
                placeholder={t("appointments.driver_name")}
                as={NCAutocomplete}
                options={
                  allData.map(item => ({
                    id: item?.id,
                    name: item?.name
                  })) || []
                }
                name="driver"
                onChange={v => {
                  formik.setFieldValue("driver", v)
                  fetchDriverSchedule(v)
                }}
                loading={isLoading || loadingSchedule}
                onSearch={v => setSearch(v)}
                required={true}
                error={formik?.errors?.driver}
              />
            </Grid>

            {/* Effective From Date */}
            <Grid item xs={12}>
              <NCText style={{ color: "#9CA3AF", fontSize: "12px", marginBottom: "4px" }}>
                Effective From
              </NCText>
              <FormField
                as={NCDatePicker}
                name="effective_from"
                onChange={v => formik.setFieldValue("effective_from", v)}
                min={new Date().toISOString().split("T")[0]}
                required={true}
                error={formik?.errors?.effective_from}
              />
              <NCText style={{ color: "#9CA3AF", fontSize: "12px", marginTop: "4px", fontStyle: "italic" }}>
                Times are shown in the driver's timezone ({driverTz})
              </NCText>
            </Grid>

            <Grid item xs={12}>
              <Divider style={{ margin: "8px 0" }} />
            </Grid>

            {/* Weekly Schedule */}
            <Grid item xs={12}>
              <NCText className={cls.sectionTitle}>Weekly Schedule</NCText>

              {DAYS_OF_WEEK.map(day => (
                <Box key={day.id} className={cls.dayRow}>
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                    <NCText className={cls.dayLabel}>{day.name}</NCText>
                    <Box display="flex" gap={1}>
                      <NCButton
                        type="button"
                        variant="text"
                        size="small"
                        onClick={() => handleAddTimeSlot(day.id)}
                        className={cls.addSlotBtn}
                        startIcon={<NCSvgIcon component={AddIcon} style={{ width: 16, height: 16 }} />}
                        style={{ whiteSpace: "nowrap" }}
                      >
                        Add Slot
                      </NCButton>
                      <NCButton
                        type="button"
                        variant="text"
                        size="small"
                        onClick={() => handleClearDay(day.id)}
                        style={{ fontSize: "11px", padding: "4px 8px", color: "#EF4444", whiteSpace: "nowrap" }}
                      >
                        Clear
                      </NCButton>
                    </Box>
                  </Box>

                  {formik.values.weekly_slots[day.id]?.map((slot, slotIndex) => (
                    <Box key={`${day.id}-${slotIndex}-${slot.start_time}-${slot.end_time}`} className={cls.timeSlot}>
                      <FormField
                        as={NCTimePicker}
                        style={{ flex: 1 }}
                        name={`weekly_slots.${day.id}[${slotIndex}].start_time`}
                        placeholder="Start"
                        value={slot.start_time}
                        onChange={v => handleTimeChange(day.id, slotIndex, "start_time", v)}
                      />
                      <NCText style={{ color: "#9CA3AF" }}>-</NCText>
                      <FormField
                        as={NCTimePicker}
                        style={{ flex: 1 }}
                        name={`weekly_slots.${day.id}[${slotIndex}].end_time`}
                        placeholder="End"
                        value={slot.end_time}
                        onChange={v => handleTimeChange(day.id, slotIndex, "end_time", v)}
                      />
                      {formik.values.weekly_slots[day.id].length > 1 && (
                        <IconButton
                          type="button"
                          size="small"
                          onClick={() => handleRemoveTimeSlot(day.id, slotIndex)}
                          style={{ padding: "4px" }}
                        >
                          <NCSvgIcon component={DeleteIcon} style={{ width: 18, height: 18, color: "#EF4444" }} />
                        </IconButton>
                      )}
                    </Box>
                  ))}
                </Box>
              ))}
            </Grid>

            <Grid item xs={12}>
              <Divider style={{ margin: "8px 0" }} />
            </Grid>

            {/* Exception Dates */}
            <Grid item xs={12}>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                <NCText className={cls.sectionTitle}>Exception Dates</NCText>
                <Box
                  onClick={handleAddException}
                  style={{ 
                    background: "#1EAAC4", 
                    color: "#FFFFFF",
                    width: "28px",
                    height: "28px",
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: "pointer"
                  }}
                >
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M8 1.5V14.5M14.5 8H1.5" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </Box>
              </Box>

              {formik.values.exception_dates.length === 0 && (
                <NCText style={{ color: "#9CA3AF", fontSize: "12px", fontStyle: "italic" }}>
                  No exceptions added. Click + to add unavailable dates or custom hours.
                </NCText>
              )}

              {formik.values.exception_dates.map((exception, index) => (
                <Box key={index} className={cls.exceptionCard}>
                  <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1}>
                    <Box flex={1}>
                      <FormField
                        as={NCDatePicker}
                        name={`exception_dates[${index}].date`}
                        placeholder="Select Date"
                        disablePast
                        onChange={v => formik.setFieldValue(`exception_dates[${index}].date`, v)}
                        style={{ marginBottom: "8px" }}
                      />
                      
                      <FormField
                        as={NCAutocomplete}
                        name={`exception_dates[${index}].type`}
                        placeholder="Exception Type"
                        options={[
                          { id: "unavailable", name: "Unavailable (Day Off)" },
                          { id: "custom_hours", name: "Custom Hours" }
                        ]}
                        onChange={v => formik.setFieldValue(`exception_dates[${index}].type`, v)}
                        style={{ marginBottom: "8px" }}
                      />

                      {exception.type === "custom_hours" && (
                        <Box display="flex" gap={1} alignItems="center">
                          <FormField
                            as={NCTimePicker}
                            style={{ flex: 1 }}
                            name={`exception_dates[${index}].start_time`}
                            placeholder="Start Time"
                            onChange={v => handleExceptionTimeChange(index, "start_time", v)}
                          />
                          <NCText>-</NCText>
                          <FormField
                            as={NCTimePicker}
                            style={{ flex: 1 }}
                            name={`exception_dates[${index}].end_time`}
                            placeholder="End Time"
                            onChange={v => handleExceptionTimeChange(index, "end_time", v)}
                          />
                        </Box>
                      )}
                    </Box>
                    <IconButton
                      type="button"
                      size="small"
                      onClick={() => handleRemoveException(index)}
                      style={{ padding: "4px", marginLeft: "8px" }}
                    >
                      <NCSvgIcon component={DeleteIcon} style={{ width: 18, height: 18, color: "#EF4444" }} />
                    </IconButton>
                  </Box>
                </Box>
              ))}
            </Grid>
          </Grid>
        </Box>
    </FormikProvider>
  )

  return (
    <NCModal
      open={modalState}
      title={t("appointments.add_availability")}
      text={requestModal}
      onClose={() => handleClose()}
      submitButton={
        <NCButton
          variant="contained"
          color="primary"
          disableElevation
          onClick={formik.handleSubmit}
          style={{ width: "auto" }}
        >
          {t("common.save")}
        </NCButton>
      }
    />
  )
}

export default AppointmentModal
